import React, { useState } from 'react';

function App() {
  const [activeTab, setActiveTab] = useState('birthChart');
  
  // Birth Chart states
  const [formData, setFormData] = useState({
    dateOfBirth: '',
    gender: ''
  });
  const [dateInputs, setDateInputs] = useState({ year: '', month: '', day: '' });
  const [birthChart, setBirthChart] = useState(null);
  // const [dckDigitCounts, setDckDigitCounts] = useState({});
  const [dckContributions, setDckContributions] = useState({});
  const [originalDigitCounts, setOriginalDigitCounts] = useState({});
  const [includeDCK, setIncludeDCK] = useState(true);
  const [calculations, setCalculations] = useState(null);

  // Name Number Calculator states
  const [nameInput, setNameInput] = useState('');
  const [nameNumber, setNameNumber] = useState(null);
  // const [nameCalculation, setNameCalculation] = useState(null);

  // Letter to number mapping
  const letterToNumber = {
    'A': 1, 'I': 1, 'J': 1, 'Q': 1, 'Y': 1,
    'B': 2, 'K': 2, 'R': 2,
    'C': 3, 'G': 3, 'L': 3, 'S': 3,
    'D': 4, 'M': 4, 'T': 4,
    'E': 5, 'H': 5, 'N': 5, 'X': 5,
    'U': 6, 'V': 6, 'W': 6,
    'O': 7, 'Z': 7,
    'F': 8, 'P': 8
  };

  const calculateNameNumber = (name) => {
    if (!name.trim()) return null;

    const cleanName = name.replace(/[^A-Za-z]/g, '').toUpperCase();
    if (cleanName.length === 0) return null;

    let sum = 0;
    const letterValues = [];

    for (let i = 0; i < cleanName.length; i++) {
      const letter = cleanName[i];
      const number = letterToNumber[letter];
      
      if (number) {
        sum += number;
        letterValues.push({ letter, number });
      }
    }

    // Reduce to single digit
    let finalNumber = sum;
    const reductionSteps = [sum];
    
    while (finalNumber > 9) {
      const digits = finalNumber.toString().split('').map(Number);
      finalNumber = digits.reduce((acc, digit) => acc + digit, 0);
      reductionSteps.push(finalNumber);
    }

    return {
      originalName: name,
      cleanName,
      letterValues,
      sum,
      finalNumber,
      reductionSteps
    };
  };

  const handleNameInputChange = (e) => {
    const value = e.target.value;
    setNameInput(value);
    
    if (value.trim()) {
      const result = calculateNameNumber(value);
      setNameNumber(result);
    } else {
      setNameNumber(null);
    }
   };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle custom date inputs
    if (['year', 'month', 'day'].includes(name)) {
      const numericValue = value.replace(/\D/g, ''); // Only allow digits
      
      setDateInputs(prev => {
        const newInputs = { ...prev, [name]: numericValue };
        
        // Auto-advance logic
        if (name === 'year' && numericValue.length === 4) {
          // Move to month after 4 digits
          document.getElementById('month').focus();
        } else if (name === 'month' && numericValue.length === 2) {
          // Move to day after 2 digits
          document.getElementById('day').focus();
        }
        
        // Update the main dateOfBirth field
        const formattedDate = `${newInputs.year}-${newInputs.month.padStart(2, '0')}-${newInputs.day.padStart(2, '0')}`;
        setFormData(prev => ({ ...prev, dateOfBirth: formattedDate }));
        
        return newInputs;
      });
      return;
    }
    
    // Handle other form fields
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateKeyDown = (e, fieldName) => {
    // Allow backspace, delete, arrow keys, tab
    if ([8, 9, 37, 38, 39, 40, 46].includes(e.keyCode)) {
      return;
    }
    
    // Only allow digits
    if (!/\d/.test(e.key)) {
      e.preventDefault();
    }
    
    // Handle backspace to go to previous field
    if (e.keyCode === 8 && e.target.value === '') {
      if (fieldName === 'month') {
        document.getElementById('year').focus();
      } else if (fieldName === 'day') {
        document.getElementById('month').focus();
      }
    }
  };

  const calculateBirthChart = (dateOfBirth, gender, currentIncludeDCK = includeDCK) => {
    // Parse the date manually - dateOfBirth is in YYYY-MM-DD format from the input
    const [year, month, day] = dateOfBirth.split('-');
    
    // Debug logging
    console.log('Input dateOfBirth:', dateOfBirth);
    console.log('Year:', year);
    console.log('Month:', month);
    console.log('Day:', day);

    // Format date as dd-mm-yyyy for display
    const formattedDate = `${day}-${month}-${year}`;
    
    // Get all digits from the date (dd-mm-yyyy format for calculation)
    const allDigits = (day + month + year).split('').map(Number);
    
    // Exclude zeroes
    const digitsWithoutZeros = allDigits.filter(digit => digit !== 0);
    
    // Calculate D (Driver) = Sum of Digits of Day
    const dayDigits = day.split('').map(Number);
    let D = dayDigits.reduce((sum, digit) => sum + digit, 0);
    
    // Calculate C (Conductor) = Sum of all digits in DOB, reduced to single digit
    const totalSum = allDigits.reduce((sum, digit) => sum + digit, 0);
    let C = totalSum > 9 ? (Math.floor(totalSum / 10) + (totalSum % 10)) : totalSum;
    
    // Calculate K (Kua) based on gender
    const yearDigits = year.split('').map(Number);
    let yearSum = yearDigits.reduce((sum, digit) => sum + digit, 0);
    
    // Keep adding digits until it becomes a single digit
    while (yearSum > 9) {
      yearSum = yearSum.toString().split('').map(Number).reduce((sum, digit) => sum + digit, 0);
    }
    
    let K;
    
    if (gender === 'Male') {
      K = 11 - yearSum;
    } else {
      K = 4 + yearSum;
    }
    
    // Ensure all calculations are single digits
    D = D > 9 ? (Math.floor(D / 10) + (D % 10)) : D;
    C = C > 9 ? (Math.floor(C / 10) + (C % 10)) : C;
    K = K > 9 ? (Math.floor(K / 10) + (K % 10)) : K;
    
    // Store calculations for display
    setCalculations({
      formattedDate,
      digitsWithoutZeros,
      D,
      C,
      K,
      yearSum
    });
    
    console.log('D, C, K values:', { D, C, K });
    console.log('includeDCK in calculation:', currentIncludeDCK);
    
    // Combine all calculations: digits without zeros + D + C + K (if enabled)
    let finalDigits = [...digitsWithoutZeros];
    let dckDigits = [];
    let dckContributions = {}; // Track which D, C, K values contributed to each digit
    let originalDigitCounts = {}; // Track counts from original date digits only
    
    // Count original digits first
    digitsWithoutZeros.forEach(digit => {
      if (digit >= 1 && digit <= 9) {
        originalDigitCounts[digit] = (originalDigitCounts[digit] || 0) + 1;
      }
    });
    
    if (currentIncludeDCK) {
      finalDigits = [...finalDigits, D, C, K];
      dckDigits = [D, C, K];
      
      // Track contributions from D, C, K
      if (D >= 1 && D <= 9) {
        if (!dckContributions[D]) dckContributions[D] = [];
        dckContributions[D].push(0); // 0 = D
      }
      if (C >= 1 && C <= 9) {
        if (!dckContributions[C]) dckContributions[C] = [];
        dckContributions[C].push(1); // 1 = C
      }
      if (K >= 1 && K <= 9) {
        if (!dckContributions[K]) dckContributions[K] = [];
        dckContributions[K].push(2); // 2 = K
      }
    }
    
    console.log('DCK contributions:', dckContributions);
    
    // Count occurrences of each digit (1-9)
    const digitCounts = {};
    const dckDigitCounts = {};
    for (let i = 1; i <= 9; i++) {
      digitCounts[i] = 0;
      dckDigitCounts[i] = 0;
    }
    
    finalDigits.forEach(digit => {
      if (digit >= 1 && digit <= 9) {
        digitCounts[digit]++;
      }
    });
    
    dckDigits.forEach(digit => {
      if (digit >= 1 && digit <= 9) {
        dckDigitCounts[digit]++;
      }
    });
    
    return { digitCounts, dckDigitCounts, dckContributions, originalDigitCounts };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.dateOfBirth && formData.gender) {
      // Validate date format
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(formData.dateOfBirth)) {
        alert('Please enter a valid date in YYYY-MM-DD format');
        return;
      }
      
      const { digitCounts, dckContributions, originalDigitCounts } = calculateBirthChart(formData.dateOfBirth, formData.gender);
      setBirthChart(digitCounts);
      // setDckDigitCounts(dckDigitCounts);
      setDckContributions(dckContributions);
      setOriginalDigitCounts(originalDigitCounts);
      // setShowChart(true); // This state is removed, so this line is removed
    }
  };

  const handleReset = () => {
    setFormData({
      dateOfBirth: '',
      gender: ''
    });
    setDateInputs({
      year: '',
      month: '',
      day: ''
    });
    setBirthChart(null);
    // setDckDigitCounts({}); // Changed to empty object
    setDckContributions({}); // Changed to empty object
    setOriginalDigitCounts({}); // Changed to empty object
    // setShowChart(false); // This state is removed, so this line is removed
    setCalculations(null);
    setIncludeDCK(true);
  };

  // Lo Shu Square layout (magic square format)
  const loShuLayout = [
    [4, 9, 2],
    [3, 5, 7],
    [8, 1, 6]
  ];

  const renderChart = () => {
    if (!birthChart) return null;

    return (
      <div>
        <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">
          Your Birth Chart Results
        </h2>
        
        {/* Calculations Display */}
        {calculations && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-semibold mb-3 text-gray-700">Calculations</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <p><strong>Date:</strong> {calculations.formattedDate}</p>
                <p><strong>Digits without zeros:</strong> {calculations.digitsWithoutZeros.join(', ')}</p>
                <p><strong>Year sum:</strong> {calculations.yearSum}</p>
              </div>
              <div>
                <p><strong>D (Driver):</strong> {calculations.D}</p>
                <p><strong>C (Conductor):</strong> {calculations.C}</p>
                <p><strong>K (Kua):</strong> {calculations.K}</p>
              </div>
            </div>
          </div>
        )}

        {/* Birth Chart Results Grid */}
        <div>
          <h3 className="text-lg font-semibold text-center mb-3 text-gray-700">
            Your Birth Chart Results
          </h3>
          
          {/* D, C, K Variables and Checkbox */}
          <div className="mb-6 flex items-center justify-center gap-6">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="includeDCK"
                checked={includeDCK}
                onChange={(e) => {
                  const newIncludeDCK = e.target.checked;
                  console.log('Checkbox changed to:', newIncludeDCK);
                  setIncludeDCK(newIncludeDCK);
                  // Recalculate birth chart immediately when checkbox changes
                  if (formData.dateOfBirth && formData.gender) {
                    console.log('Recalculating with includeDCK:', newIncludeDCK);
                    const { digitCounts, dckContributions, originalDigitCounts } = calculateBirthChart(formData.dateOfBirth, formData.gender, newIncludeDCK);
                    console.log('New digitCounts:', digitCounts);
                    // console.log('New dckDigitCounts:', dckDigitCounts);
                    setBirthChart(digitCounts);
                    // setDckDigitCounts(dckDigitCounts);
                    setDckContributions(dckContributions);
                    setOriginalDigitCounts(originalDigitCounts);
                  } else {
                    console.log('No date/gender available for recalculation');
                  }
                }}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <label htmlFor="includeDCK" className="text-sm font-medium text-gray-700">
                Include D, C, K variables
              </label>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6 sm:gap-8">
            {/* Birth Chart Grid */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3 w-full max-w-xs sm:max-w-none sm:w-auto">
              {loShuLayout.map((row, rowIndex) =>
                row.map((digit, colIndex) => (
                  <div
                    key={`result-${rowIndex}-${colIndex}`}
                    className="aspect-square border-2 border-gray-300 rounded-lg flex items-center justify-center bg-white shadow-sm w-full sm:w-36 sm:h-36"
                  >
                    {(() => {
                       const originalCount = originalDigitCounts[digit] || 0;
                       // const dckCount = dckDigitCounts[digit] || 0;
                       const contributions = dckContributions[digit] || [];
                       
                       if (birthChart[digit] > 0) {
                         const spans = [];
                         
                         // Add original digits in blue
                         for (let i = 0; i < originalCount; i++) {
                           spans.push(
                             <span key={`original-${i}`} className="text-blue-600">
                               {digit}
                             </span>
                           );
                         }
                         
                         // Add DCK digits in orange with appropriate opacity
                         contributions.forEach((contribution, index) => {
                           const opacity = contribution === 0 ? 'opacity-100' : 
                                         contribution === 1 ? 'opacity-60' : 
                                         contribution === 2 ? 'opacity-30' : '';
                           spans.push(
                             <span key={`dck-${index}`} className={`text-orange-600 ${opacity}`}>
                               {digit}
                             </span>
                           );
                         });
                         
                         return (
                           <div className="text-2xl sm:text-4xl font-bold">
                             {spans}
                           </div>
                         );
                       } else {
                         return '';
                       }
                     })()}
                  </div>
                ))
              )}
            </div>
            
            {/* D, C, K Variables Display */}
            {calculations && (
              <div className="flex flex-row sm:flex-col gap-4 sm:gap-6 justify-center">
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-600 bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-2 opacity-100">
                    {calculations.D}
                  </div>
                  <div className="text-sm font-medium text-gray-700">D (Driver)</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-600 bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-2 opacity-60">
                    {calculations.C}
                  </div>
                  <div className="text-sm font-medium text-gray-700">C (Conductor)</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-600 bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-2 opacity-30">
                    {calculations.K}
                  </div>
                  <div className="text-sm font-medium text-gray-700">K (Kua)</div>
                </div>
              </div>
            )}
          </div>
          <div className="mt-4 text-center text-sm text-gray-600">
            <p>Numbers that appear in your birth chart calculations are shown above</p>
            <p>Repeated numbers indicate multiple occurrences</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl sm:text-4xl font-bold text-center mb-8 text-gray-800">
          Numerology Calculator
        </h1>
        
        {/* Tabbed Interface */}
        <div className="mb-8">
          <div className="flex border-b border-gray-200 justify-center">
            <button
              onClick={() => setActiveTab('birthChart')}
              className={`py-3 px-4 sm:px-6 text-sm sm:text-base font-semibold text-gray-700 transition-colors ${activeTab === 'birthChart' ? 'border-b-2 border-purple-600 text-purple-600' : 'hover:text-purple-600'}`}
            >
              Birth Chart
            </button>
            <button
              onClick={() => setActiveTab('nameNumber')}
              className={`py-3 px-4 sm:px-6 text-sm sm:text-base font-semibold text-gray-700 transition-colors ${activeTab === 'nameNumber' ? 'border-b-2 border-purple-600 text-purple-600' : 'hover:text-purple-600'}`}
            >
              Name Number
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-8">
          {/* Birth Chart Reference and Form */}
          {activeTab === 'birthChart' && (
            <div>
              {/* Lo Shu Square Reference - Before Input Fields */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-center mb-4 text-gray-700">
                  Lo Shu Square Reference
                </h3>
                <div className="grid grid-cols-3 gap-2 max-w-md mx-auto">
                  {loShuLayout.map((row, rowIndex) => 
                    row.map((digit, colIndex) => (
                      <div
                        key={`${rowIndex}-${colIndex}`}
                        className="aspect-square border-2 border-gray-400 rounded-lg flex items-center justify-center bg-gray-100 shadow-sm"
                      >
                        <div className="text-2xl font-bold text-gray-700">
                          {digit}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <p className="text-center text-sm text-gray-600 mt-3">
                  Your birth chart will follow this layout pattern
                </p>
              </div>

              {/* Birth Chart Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-2">
                    Year (YYYY)
                  </label>
                  <input
                    type="text"
                    id="year"
                    name="year"
                    value={dateInputs.year}
                    onChange={handleInputChange}
                    onKeyDown={(e) => handleDateKeyDown(e, 'year')}
                    maxLength="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                <div>
                  <label htmlFor="month" className="block text-sm font-medium text-gray-700 mb-2">
                    Month (MM)
                  </label>
                  <input
                    type="text"
                    id="month"
                    name="month"
                    value={dateInputs.month}
                    onChange={handleInputChange}
                    onKeyDown={(e) => handleDateKeyDown(e, 'month')}
                    maxLength="2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                <div>
                  <label htmlFor="day" className="block text-sm font-medium text-gray-700 mb-2">
                    Day (DD)
                  </label>
                  <input
                    type="text"
                    id="day"
                    name="day"
                    value={dateInputs.day}
                    onChange={handleInputChange}
                    onKeyDown={(e) => handleDateKeyDown(e, 'day')}
                    maxLength="2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                <div>
                  <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-2">
                    Gender
                  </label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>

                <div className="flex gap-4">
                  <button
                    type="submit"
                    className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors"
                  >
                    Generate Birth Chart
                  </button>
                  <button
                    type="button"
                    onClick={handleReset}
                    className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                  >
                    Reset
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Name Number Calculator Reference and Form */}
          {activeTab === 'nameNumber' && (
            <div>
              {/* Letter to Number Reference Table - Before Input Fields */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-center mb-4 text-gray-700">
                  Letter to Number Mapping
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                  {Object.entries({
                    1: ['A', 'I', 'J', 'Q', 'Y'],
                    2: ['B', 'K', 'R'],
                    3: ['C', 'G', 'L', 'S'],
                    4: ['D', 'M', 'T'],
                    5: ['E', 'H', 'N', 'X'],
                    6: ['U', 'V', 'W'],
                    7: ['O', 'Z'],
                    8: ['F', 'P']
                  }).map(([number, letters]) => (
                    <div key={number} className="bg-gray-50 p-3 rounded-lg text-center">
                      <div className="text-2xl font-bold text-purple-600 mb-1">{number}</div>
                      <div className="text-sm text-gray-600">{letters.join(', ')}</div>
                    </div>
                  ))}
                </div>
                <p className="text-center text-sm text-gray-600 mt-3">
                  Each letter corresponds to a number. Add all numbers and reduce to a single digit.
                </p>
              </div>

              {/* Name Number Calculator Form */}
              <div className="space-y-6">
                <div>
                  <label htmlFor="nameInput" className="block text-sm font-medium text-gray-700 mb-2">
                    Enter your name (letters only):
                  </label>
                  <input
                    type="text"
                    id="nameInput"
                    value={nameInput}
                    onChange={handleNameInputChange}
                    placeholder="e.g., John Doe"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-lg"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Results Section */}
          <div className="mt-8">
            {activeTab === 'birthChart' && birthChart && renderChart()}
            {activeTab === 'nameNumber' && nameNumber && (
              <div className="bg-gray-50 rounded-lg shadow-sm p-6">
                <div className="space-y-4">
                  {/* Final Result */}
                  <div className="text-center bg-white p-6 rounded-lg">
                    <div className="text-4xl font-bold text-purple-600 mb-2">
                      {nameNumber.finalNumber}
                    </div>
                    <div className="text-lg text-gray-700">Your Name Number</div>
                  </div>

                  {/* Calculation Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-700 mb-2">Name Details</h4>
                      <p className="text-sm text-gray-600 mb-1">
                        <span className="font-medium">Original:</span> {nameNumber.originalName}
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Clean:</span> {nameNumber.cleanName}
                      </p>
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-700 mb-2">Calculation Steps</h4>
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Reduction:</span> {nameNumber.reductionSteps.join(' → ')}
                      </p>
                    </div>
                  </div>

                  {/* Letter Values */}
                  <div className="bg-white p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-700 mb-3">Letter Values</h4>
                    <div className="flex flex-wrap gap-2">
                      {nameNumber.letterValues.map((item, index) => (
                        <div key={index} className="bg-gray-50 px-3 py-2 rounded-lg border border-gray-200 flex items-center gap-2">
                          <span className="font-bold text-purple-600">{item.letter}</span>
                          <span className="text-gray-500">→</span>
                          <span className="font-bold text-gray-700">{item.number}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App; 